import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useNavigate } from 'react-router-dom';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.categorySlug === activeSlug);
    if (sortBy === 'newest') {
      result = [...result].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const catSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name}漫画 - 073漫画网`,
    description: `${activeCategory.name}漫画免费在线阅读，${activeCategory.description}`,
    isPartOf: { '@type': 'WebSite', name: '073漫画网', url: 'https://073mh.com' },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(catSchema)}</script>

      <Navbar />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${activeCategory.name}漫画` },
          ]}
        />

        {/* Category Header */}
        <section className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {activeCategory.name}漫画
          </h1>
          <p className="text-foreground-500 text-sm">{activeCategory.description}</p>
        </section>

        {/* Category Tabs */}
        <section className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </section>

        {/* Sort Options */}
        <section className="flex items-center gap-3 mb-6">
          <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
          {(['popular', 'newest', 'rating'] as const).map((key) => (
            <button
              key={key}
              onClick={() => setSortBy(key)}
              className={`px-3 py-1.5 rounded-md text-sm transition-all cursor-pointer whitespace-nowrap ${
                sortBy === key
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
              }`}
            >
              {key === 'popular' ? '最热门' : key === 'newest' ? '最新更新' : '评分最高'}
            </button>
          ))}
        </section>

        {/* Comic Grid */}
        <section className="mb-14">
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              <p className="text-foreground-500 mt-3">该分类暂无漫画，敬请期待</p>
            </div>
          )}
        </section>

        {filteredComics.length > 0 && (
          <section className="mb-14">
            <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              为什么选择073漫画网看{activeCategory.name}漫画？
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-translate-2 text-primary-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">高清翻译</h3>
                <p className="text-xs text-foreground-500">专业翻译团队精心汉化，保证阅读体验流畅自然</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-timer-flash-line text-accent-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">极速更新</h3>
                <p className="text-xs text-foreground-500">同步韩国更新进度，第一时间带来最新{activeCategory.name}漫画章节</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                  <i className="ri-smartphone-line text-secondary-500 text-lg"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">多端适配</h3>
                <p className="text-xs text-foreground-500">电脑、平板、手机全平台适配，随时随地畅享{activeCategory.name}漫画</p>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}